import React from 'react';
import SVG from 'react-inlinesvg';
import { useQuery } from '@apollo/client';
import { formatWithCommas } from '@perch-labs/warbler';
import Star from '/images/icons/star.svg';
import { kwhToMwh } from 'utils/format';
import { Card, Container, LabelAndTooltip } from 'styles';
import { Icon, Text, Tooltip } from '@perch-labs/shrike';
import { copyFor } from 'config';
import { Map } from 'components/map';
import {
  DataAndLabel,
  DataAndLabelHorizontalList,
} from 'components/data-and-label';
import { GET_VNM_SOLAR_PROJECTS_BY_STATE } from 'pages/solar/states/with-utility-accounts/active/active.api';
import { MarkerIcon, ActiveMarkerIcon } from 'components/map/map.style';
import { Legend } from './state-solar-farms.style';
import { MarkerPopup } from './map-marker-popup.component';
import { InvisibleMapTable } from './invisible-map-table';

const getCopy = copyFor('solar.stateStats');
const showYourFarm = (
  projects: Array<VnmSolarProject>,
  vnmSolarProjectId: number | undefined
) => {
  if (projects) {
    const yourProject = projects.find(
      project => project.id === vnmSolarProjectId
    );
    return yourProject && yourProject.latitude && yourProject.longitude;
  } else {
    return false;
  }
};

interface Props {
  stateFullName?: string;
  stateId?: number;
  vnmSolarProjectsStatistics: GetStateVnmStatsQuery['plan']['utilityAccounts'][number]['utility']['state']['vnmSolarProjectsStatistics'];
  vnmSolarProjectId?: number;
}

export const StateSolarFarms = ({
  stateFullName,
  stateId,
  vnmSolarProjectsStatistics,
  vnmSolarProjectId,
}: Props) => {
  const { totalNumberOfMembers, numberOfProjects, expectedKwh } =
    vnmSolarProjectsStatistics;
  const { data } = useQuery(GET_VNM_SOLAR_PROJECTS_BY_STATE, {
    variables: { stateId },
  });

  const legend = (
    <Legend>
      <Text textStyle="heading500" tag="h3" margin={{ bottom: '6px' }}>
        {getCopy('statsMap.legend.title')}
      </Text>
      <Container flex alignItems="center" margin={{ bottom: '8px' }}>
        <ActiveMarkerIcon />
        <Text margin={{ left: '8px' }}>{getCopy('statsMap.legend.live')}</Text>
      </Container>
      <Container flex alignItems="center">
        <MarkerIcon />
        <Text margin={{ left: '8px' }}>
          {getCopy('statsMap.legend.comingSoon')}
        </Text>
      </Container>
      {showYourFarm(data?.vnmSolarProjectsByState, vnmSolarProjectId) && (
        <Container flex alignItems="center" margin={{ top: '8px' }}>
          <SVG src={Star} height="16px" width="16px" />
          <Text margin={{ left: '8px' }}>
            {getCopy('statsMap.legend.yourFarm')}
          </Text>
        </Container>
      )}
    </Legend>
  );

  const megawattNode = (
    <LabelAndTooltip>
      {getCopy('stats.expectedMWh')}
      <Tooltip
        content={getCopy('stats.mwhTooltip', { stateFullName })}
        tooltipId="MWh"
        label={getCopy('stats.mwhTooltipLabel')}
      >
        <Icon icon="InfoCircleInverted" />
      </Tooltip>
    </LabelAndTooltip>
  );

  return (
    <Card margin={{ top: '24px' }}>
      <Card.Content>
        <Text
          textStyle="heading700"
          tag="h2"
          margin={{ bottom: '24px', top: '40px' }}
        >
          {getCopy('title', { stateFullName })}
        </Text>
        <Text margin={{ bottom: '40px' }} aria-hidden="true">
          {getCopy('subTitle', { stateFullName })}
        </Text>
        <DataAndLabelHorizontalList margin={{ bottom: '32px' }}>
          <DataAndLabel
            labelTextStyle="paragraph400"
            label={getCopy('stats.solarMembers')}
            value={formatWithCommas(totalNumberOfMembers || 0)}
          />
          <DataAndLabel
            labelTextStyle="paragraph400"
            label={getCopy('stats.solarFarms')}
            value={numberOfProjects?.toString()}
          />
          <DataAndLabel
            label={megawattNode}
            value={kwhToMwh(expectedKwh || 0)}
          />
        </DataAndLabelHorizontalList>
        <Map
          markers={data?.vnmSolarProjectsByState.filter(
            solarProject =>
              solarProject.latitude !== null && solarProject.longitude !== null
          )}
          legend={legend}
          popupChild={MarkerPopup}
          vnmSolarProjectId={vnmSolarProjectId}
        />
        <InvisibleMapTable
          projectData={data?.vnmSolarProjectsByState || []}
          vnmSolarProjectId={vnmSolarProjectId}
        />
      </Card.Content>
    </Card>
  );
};
